import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MoveHorizontal } from "lucide-react";
import { KICKSTARTER_URL } from "@/lib/links";

const EASE = [0.16, 1, 0.3, 1];

const MONTHS = [
  { img: "/images/month-01.jpg", month: "January", ingredient: "Calçots", place: "Valls, Catalonia", caption: "We start the year in Catalonia to celebrate the Calçotada. This is more than a meal; it is a ritual of smoke, charred onions, and communal messy eating that serves as a cornerstone of Catalan identity. Grapevine char only." },
  { img: "/images/month-02.jpg", month: "February", ingredient: "Tamales", place: "CDMX, Mexico", caption: "On February 2 (Día de la Candelaria), those who found the Niño Dios figurine in the January 6 Rosca de Reyes have to “pay their debt” by hosting a large tamal feast in Mexico City. The official end of the Christmas season means cooking, sharing and eating with family and friends." },
  { img: "/images/month-03.jpg", month: "March", ingredient: "Maple Syrup", place: "Québec, Canada", caption: "For March, we head to Quebec — where I happen to live. The first good syrup usually appears around March 5–15, depending on the freeze-thaw cycle. Early-season syrup is pale, golden and floral; by late March, it gets darker, richer and more savory." },
  { img: "/images/month-04.jpg", month: "April", ingredient: "White Asparagus", place: "Beelitz, Germany", caption: "April brings Europe's “White Gold”: Beelitzer Spargel, Germany's prized white asparagus. The season is just getting started, with Spargelzeit running through June 24 — when, with typically German punctuality, the asparagus season abruptly ends." },
  { img: "/images/month-05.jpg", month: "May", ingredient: "Caș & Brânză", place: "Carpathians, Romania", caption: "In May we travel into the Romanian side of the Carpathians, when shepherds move their flocks into the high mountain pastures and begin making fresh sheep's cheese. The milk changes immediately, infused by the hundreds of wildflowers and herbs the sheep graze along the way." },
  { img: "/images/month-06.jpg", month: "June", ingredient: "Alphonso Mango", place: "Ratnagiri, India", caption: "While mangoes exist globally, the true Ratnagiri Alphonso is a short-lived Indian obsession. For a few precious weeks in June, Ratnagiri revolves around the fruit; it's the time to eat Aamras made from perfectly ripe local mangoes, before the season disappears again." },
  { img: "/images/month-07.jpg", month: "July", ingredient: "Cavaillon Melon", place: "Provence, France", caption: "Cavaillon melon (Provence) — the sugar-density peak of July. If the fruit doesn't smell like a perfume shop from three feet away, it's just a cantaloupe. Local lore dictates that a perfect melon has exactly 10 dark green vertical stripes. 9 is premature; 11 is a fermentation risk." },
  { img: "/images/month-08.jpg", month: "August", ingredient: "Hatch Green Chiles", place: "New Mexico, USA", caption: "In August, we head to New Mexico for Hatch Green Chile. The scent of charred skins and propane from Mesilla Valley meets Santa Fe fine dining, where chile gets grand-cru treatment. Authentic Hatch chile is flame-roasted until blistered and ready to slip its skin." },
  { img: "/images/month-09.jpg", month: "September", ingredient: "Matsutake", place: "Kyoto, Japan", caption: "These are the “truffles of the East.” They cannot be cultivated. The scent is ephemeral — highly spicy and woody. Eating them grilled over charcoal in a Kyoto ryokan during these weeks of September is a pinnacle food experience, a Japanese atmospheric event." },
  { img: "/images/month-10.jpg", month: "October", ingredient: "Hairy Crab Roe", place: "Shanghai, China", caption: "By late September, the female crab's roe is still soft and semi-liquid. By October, it reaches its peak: dense, granular, intensely flavored, and deep orange-red. The roe should hold its shape when the shell is opened, with the firm, crumbly texture of a hard-boiled egg yolk." },
  { img: "/images/month-11.jpg", month: "November", ingredient: "White Truffle", place: "Alba, Italy", caption: "It's an expensive, fleeting burst of aroma, but in November, this corner of Alba in Piedmont is the only place that matters — tuber magnatum pico, the legendary white truffle at its aromatic peak. Eat them there: tajarin with butter, truffle shaved tableside, weighed before and after — no kitchen substitutions." },
  { img: "/images/month-12.jpg", month: "December", ingredient: "Lutefisk", place: "Trondheim, Norway", caption: "Before refrigeration, Norway learned to preserve the ocean itself. In the Arctic winter, cod was transformed by wind, time and tradition into December lutefisk — the controversial but beloved centerpiece of Christmas tables." },
];

export default function Gallery() {
  const trackRef = useRef(null);
  const [highlight, setHighlight] = useState(null);
  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * 400, behavior: "smooth" });

  useEffect(() => {
    let timer;
    const onHighlight = (e) => {
      const i = e.detail;
      const track = trackRef.current;
      const card = track?.querySelector(`[data-testid="gallery-card-${i}"]`);
      if (track && card) {
        track.scrollTo({
          left: card.offsetLeft - track.clientWidth / 2 + card.clientWidth / 2,
          behavior: "smooth",
        });
      }
      setHighlight(i);
      clearTimeout(timer);
      timer = setTimeout(() => setHighlight(null), 2600);
    };
    window.addEventListener("bookata:highlight-month", onHighlight);
    return () => {
      window.removeEventListener("bookata:highlight-month", onHighlight);
      clearTimeout(timer);
    };
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative overflow-hidden bg-sky py-24 md:py-32">
      <div className="mx-auto flex w-full max-w-[1440px] flex-wrap items-end justify-between gap-6 px-6 md:px-12">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-[11px] font-bold uppercase tracking-[0.4em] text-leaf"
          >
            Preview
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, delay: 0.08, ease: EASE }}
            className="mt-4 font-display text-[clamp(2.4rem,5.5vw,5rem)] font-extrabold uppercase leading-[0.9] tracking-tight text-navy"
          >
            Peek inside <span className="text-punch">2027</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
            className="mt-3 font-serif text-xl italic text-navy/80"
          >
            The full dozen — every month, every dish, every place.
          </motion.p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => scroll(-1)}
            data-testid="gallery-prev-button"
            aria-label="Scroll gallery left"
            className="rounded-full border-2 border-navy bg-paper p-3.5 text-navy transition-colors duration-300 hover:bg-navy hover:text-banana"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            data-testid="gallery-next-button"
            aria-label="Scroll gallery right"
            className="rounded-full border-2 border-navy bg-paper p-3.5 text-navy transition-colors duration-300 hover:bg-navy hover:text-banana"
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        data-testid="gallery-track"
        className="scrollbar-hide mt-12 flex snap-x snap-mandatory gap-8 overflow-x-auto px-6 pb-6 pt-2 md:px-12"
      >
        {MONTHS.map((m, i) => (
          <motion.article
            key={m.month}
            data-testid={`gallery-card-${i}`}
            initial={{ opacity: 0, y: 64, rotate: i % 2 ? 2.5 : -2.5 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: i * 0.08, duration: 0.75, ease: EASE }}
            className="group w-[300px] shrink-0 snap-start md:w-[380px]"
          >
            <div
              className={`border-4 bg-paper p-2.5 transition-all duration-300 ${
                highlight === i
                  ? "-translate-y-2 rotate-1 border-punch shadow-[12px_14px_0_#F2629E]"
                  : "border-navy shadow-[8px_8px_0_#2E3192] group-hover:-translate-y-2 group-hover:rotate-1 group-hover:shadow-[12px_14px_0_#2E3192]"
              }`}
            >
              <img
                src={m.img}
                alt={`${m.month} — ${m.ingredient}, ${m.place}`}
                className="aspect-[11/8.5] w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="mt-5 flex items-start justify-between gap-4">
              <div>
                <p className="font-display text-xl font-extrabold uppercase tracking-tight text-navy md:text-2xl">
                  {m.month}
                </p>
                <p className="font-serif text-lg italic text-punch">{m.ingredient}</p>
              </div>
              <p className="pt-1 text-right text-[11px] font-bold uppercase leading-relaxed tracking-[0.2em] text-navy/70">
                {m.place}
              </p>
            </div>
            <p data-testid={`gallery-caption-${i}`} className="mt-3 border-l-4 border-punch pl-3 text-base leading-relaxed text-navy md:text-lg">
              {m.caption}
            </p>
          </motion.article>
        ))}
        <motion.a
          href={KICKSTARTER_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="gallery-end-card"
          initial={{ opacity: 0, y: 64 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ delay: 0.36, duration: 0.75, ease: EASE }}
          className="group flex w-[240px] shrink-0 snap-start items-center justify-center border-4 border-dashed border-navy/40 p-8 transition-colors duration-300 hover:border-punch hover:bg-paper"
        >
          <p className="text-center font-serif text-xl italic text-navy/70 transition-colors duration-300 group-hover:text-navy">
            12 of 12 —
            <br />
            the whole year, served.
            <span className="mt-4 block font-sans text-[11px] font-bold uppercase not-italic tracking-[0.25em] text-punch">
              Back it on Kickstarter ↗
            </span>
          </p>
        </motion.a>
      </div>

      <div className="mt-4 flex items-center gap-2 px-6 text-navy/60 md:px-12">
        <MoveHorizontal className="h-4 w-4" />
        <span className="text-[11px] font-bold uppercase tracking-[0.3em]">Scroll sideways</span>
      </div>
    </section>
  );
}
